import React, { useState, useEffect } from "react";
import ReportHeader from "./ReportHeader";
import { API_URL } from "@/env";
import MediaViewer from "./MediaViewer";
import MediaSkeleton from "./MediaSkeleton";
import { type Report } from "@/lib/types";
import { setCookie } from "@/lib/cookies";
import NotFound from "@/pages/NotFound";
import useUserData from "@/hooks/useUserData";

interface ConsoleLog {
  level: string;
  message: string;
  timestamp: number;
  url?: string;
  timeOffset?: string;
}

interface NetworkLog {
  url: string;
  method: string;
  status: number;
  statusText: string;
  duration: number;
  type: string;
  timestamp: number;
  size?: number;
  error?: boolean;
  tabId?: string;
  tabUrl?: string;
}

const MediaViewerContainer: React.FC = () => {
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const { setUser } = useUserData();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    const error = urlParams.get("error");

    if (token) {
      setCookie("token", token);

      const getUser = async () => {
        try {
          if (token) {
            const res = await fetch(`${API_URL}/auth/me`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });
            const user = await res.json();

            if (user) {
              setUser({ ...user });
            }
          }
        } catch (err) {}
      };

      getUser();

      // Remove auth params, keep everything else (like id)
      urlParams.delete("token");
      urlParams.delete("username");
      urlParams.delete("profilePicture");
      // showCustomToast("Logged in succesfully", "check");
      const newSearch = urlParams.toString();
      const newUrl = newSearch
        ? `${window.location.pathname}?${newSearch}`
        : window.location.pathname;

      window.history.replaceState({}, document.title, newUrl);
      // Optionally refresh or update UI state
    } else if (error) {
      setError(`Authentication failed: ${error}`);

      // Remove only error param, keep everything else
      urlParams.delete("error");
      const newSearch = urlParams.toString();
      const newUrl = newSearch
        ? `${window.location.pathname}?${newSearch}`
        : window.location.pathname;

      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  const searchParams = new URLSearchParams(window.location.search);
  const reportId = searchParams.get("id");

  useEffect(() => {
    const fetchReport = async () => {
      if (!reportId) {
        setError("No report ID provided in URL");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_URL}/reports/${reportId}`);

        if (!response.ok) {
          throw new Error(
            `Failed to fetch report: ${response.status} ${response.statusText}`,
          );
        }

        const reportData: Report = await response.json();
        setReport(reportData);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching report:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch report");
        setLoading(false);
      }
    };

    fetchReport();
  }, [reportId]);

  // Update page title when report is loaded
  useEffect(() => {
    if (report?.title) {
      document.title = `Toolbar | ${report.title}`;
    }
  }, [report]);

  const handleEdit = (): void => {
    // console.log("Opening editor for report:", reportId);
    // TODO: Implement edit functionality
  };

  const handleShare = (url: string): void => {
    // console.log("Sharing media URL:", url);
    // TODO: Implement share functionality
    if (navigator.share) {
      navigator.share({
        title: report?.info?.url || "Shared Media",
        url: url,
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(url).then(() => {
        alert("Link copied to clipboard!");
      });
    }
  };

  // Loading state
  if (loading) {
    return <MediaSkeleton mediaType={report?.mediaType} />;
  }

  // Error state
  if (error) {
    return <NotFound />;
  }

  // No report found
  if (!report) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg max-w-md">
          <p className="text-gray-600">No report found</p>
        </div>
      </div>
    );
  }

  // Ensure mediaUrl is always an array
  const mediaUrl = Array.isArray(report.media) ? report.media : [report.media];

  const filename = mediaUrl[0]?.split("/").pop() || "media-file";

  const estimatedFileSize = 1024000;

  return (
    <div className="h-full lg:h-screen p-4 lg:overflow-y-hidden">
      <ReportHeader reportId={reportId} title={report?.title} />
      <MediaViewer
        mediaUrl={mediaUrl} // Always an array
        mediaType={report.mediaType}
        filename={filename}
        uploadDate={report.createdAt || report.updatedAt}
        fileSize={estimatedFileSize}
        onEdit={handleEdit}
        onShare={handleShare}
        report={report}
      />
    </div>
  );
};

export default MediaViewerContainer;
